/**
 * 极简安全 markdown 渲染器（铁律 #12）：
 * 先整体 HTML 转义，再只用自己生成的标签做白名单变换——任何原文 HTML 都出不来。
 * 支持：标题/粗斜体/行内代码/代码块/无序有序列表/引用/链接(仅 http(s))/表格/分割线。
 */

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function inlineMd(s: string): string {
  // 行内代码
  s = s.replace(/`([^`\n]+)`/g, (_m, c) => `<code>${c}</code>`);
  // 粗体 / 斜体
  s = s.replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>');
  s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<i>$2</i>');
  // 链接 [text](http://...)，仅放行 http/https，其它协议按纯文本
  s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  return s;
}

export function renderMarkdown(src: string): string {
  const esc = escapeHtml(src || '');
  const lines = esc.split('\n');
  const out: string[] = [];
  let i = 0;
  let inCode = false;
  let codeBuf: string[] = [];
  let listBuf: string[] = [];
  let tableBuf: string[] = [];

  const flushList = () => {
    if (!listBuf.length) return;
    out.push(`<ul>${listBuf.map((li) => `<li>${inlineMd(li)}</li>`).join('')}</ul>`);
    listBuf = [];
  };
  const flushTable = () => {
    if (tableBuf.length < 2) {
      if (tableBuf.length) out.push(`<p>${inlineMd(tableBuf.join('<br/>'))}</p>`);
      tableBuf = [];
      return;
    }
    const rows = tableBuf.filter((l) => !/^\s*\|?[\s:|-]+\|?\s*$/.test(l));
    if (!rows.length) {
      tableBuf = [];
      return;
    }
    const cells = (r: string) => r.replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
    const head = cells(rows[0]);
    const body = rows.slice(1);
    out.push(
      `<table><thead><tr>${head.map((c) => `<th>${inlineMd(c)}</th>`).join('')}</tr></thead><tbody>${
        body.map((r) => `<tr>${cells(r).map((c) => `<td>${inlineMd(c)}</td>`).join('')}</tr>`).join('')
      }</tbody></table>`
    );
    tableBuf = [];
  };

  while (i < lines.length) {
    const line = lines[i];

    if (/^\s*```/.test(line)) {
      if (inCode) {
        out.push(`<pre><code>${codeBuf.join('\n')}</code></pre>`);
        codeBuf = [];
        inCode = false;
      } else {
        flushList();
        flushTable();
        inCode = true;
      }
      i++;
      continue;
    }
    if (inCode) {
      codeBuf.push(line);
      i++;
      continue;
    }

    if (/^\s*\|.*\|\s*$/.test(line)) {
      tableBuf.push(line);
      i++;
      continue;
    } else {
      flushTable();
    }

    const h = line.match(/^(#{1,4})\s+(.+)$/);
    if (h) {
      flushList();
      const lvl = Math.min(h[1].length + 1, 5);
      out.push(`<h${lvl}>${inlineMd(h[2])}</h${lvl}>`);
      i++;
      continue;
    }

    if (/^\s*([-*+]|\d+\.)\s+/.test(line)) {
      listBuf.push(line.replace(/^\s*([-*+]|\d+\.)\s+/, ''));
      i++;
      continue;
    }
    flushList();

    if (/^\s*&gt;\s?/.test(line)) {
      out.push(`<blockquote>${inlineMd(line.replace(/^\s*&gt;\s?/, ''))}</blockquote>`);
      i++;
      continue;
    }

    if (/^\s*([-*_]\s*){3,}$/.test(line)) {
      out.push('<hr/>');
      i++;
      continue;
    }

    if (line.trim() === '') {
      i++;
      continue;
    }
    out.push(`<p>${inlineMd(line)}</p>`);
    i++;
  }
  if (inCode) out.push(`<pre><code>${codeBuf.join('\n')}</code></pre>`);
  flushList();
  flushTable();
  return out.join('');
}
